import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import {
  invoiceService,
  InvoiceType,
  Invoice,
  CONSULTATION_DEFAULTS,
} from '@/services/invoiceService';
import { Loader2, AlertCircle, Copy, Check, Send } from 'lucide-react';

interface BillPatientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientName?: string;
  onSuccess?: (invoice: Invoice) => void;
}

const INVOICE_TYPES: { value: InvoiceType; label: string; description: string }[] = [
  { value: 'consultation-15', label: '15 min', description: '$75' },
  { value: 'consultation-30', label: '30 min', description: '$125' },
  { value: 'consultation-60', label: '60 min', description: '$200' },
  { value: 'custom', label: 'Custom', description: 'Set amount' },
];

const BillPatientDialog: React.FC<BillPatientDialogProps> = ({
  open,
  onOpenChange,
  patientId,
  patientName,
  onSuccess,
}) => {
  const { clinic } = useClinic();
  const { toast } = useToast();

  const [selectedType, setSelectedType] = useState<InvoiceType>('consultation-30');
  const [customAmount, setCustomAmount] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Post-creation state
  const [createdInvoice, setCreatedInvoice] = useState<Invoice | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const displayAmount =
    selectedType === 'custom'
      ? customAmount
        ? `$${parseFloat(customAmount).toFixed(2)}`
        : '—'
      : `$${CONSULTATION_DEFAULTS[selectedType].amount.toFixed(2)}`;

  const handleClose = () => {
    if (isSubmitting || isSendingEmail) return;
    setSelectedType('consultation-30');
    setCustomAmount('');
    setDescription('');
    setNotes('');
    setError(null);
    setCreatedInvoice(null);
    setLinkCopied(false);
    onOpenChange(false);
  };

  const handleCreate = async () => {
    if (!clinic?.id) return;
    setError(null);

    if (selectedType === 'custom') {
      const amt = parseFloat(customAmount);
      if (!customAmount || isNaN(amt) || amt <= 0) {
        setError('Please enter a valid amount greater than $0.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload: Parameters<typeof invoiceService.createInvoice>[1] = {
        patient_id: patientId,
        type: selectedType,
      };
      if (selectedType === 'custom') {
        payload.amount = parseFloat(customAmount);
      }
      if (description.trim()) payload.description = description.trim();
      if (notes.trim()) payload.notes = notes.trim();

      const invoice = await invoiceService.createInvoice(clinic.id, payload);
      setCreatedInvoice(invoice);
      onSuccess?.(invoice);
      toast({
        title: 'Invoice created',
        description: 'Invoice has been created successfully.',
        variant: 'success' as any,
      });
    } catch (err: any) {
      const msg = err?.message || 'Failed to create invoice. Please try again.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!createdInvoice?.payment_link) return;
    await navigator.clipboard.writeText(createdInvoice.payment_link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleSendEmail = async () => {
    if (!clinic?.id || !createdInvoice) return;
    setIsSendingEmail(true);
    try {
      await invoiceService.sendInvoice(clinic.id, createdInvoice.id);
      toast({
        title: 'Invoice sent',
        description: 'Payment link emailed to the patient.',
        variant: 'success' as any,
      });
      handleClose();
    } catch (err: any) {
      toast({
        title: 'Failed to send',
        description: err?.message || 'Could not send invoice email.',
        variant: 'destructive',
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Bill Patient</DialogTitle>
          <DialogDescription>
            {patientName ? `Create an invoice for ${patientName}.` : 'Create a patient invoice.'}
          </DialogDescription>
        </DialogHeader>

        {!createdInvoice ? (
          <>
            <div className="space-y-5 py-2">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Consultation Type */}
              <div className="space-y-2">
                <Label>Consultation Type</Label>
                <div className="grid grid-cols-4 gap-2">
                  {INVOICE_TYPES.map(({ value, label, description: desc }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSelectedType(value)}
                      className={`flex flex-col items-center rounded-lg border px-3 py-3 text-sm transition-all ${
                        selectedType === value
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <span className="font-medium">{label}</span>
                      <span className={`mt-0.5 text-xs ${selectedType === value ? 'text-teal-500' : 'text-slate-400'}`}>
                        {desc}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              {selectedType === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="custom-amount">
                    Amount <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                      $
                    </span>
                    <Input
                      id="custom-amount"
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      value={customAmount}
                      onChange={e => setCustomAmount(e.target.value)}
                      className="pl-7"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              )}

              {/* Amount Preview */}
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                <span className="text-sm text-slate-500">Invoice Amount</span>
                <span className="text-lg font-semibold text-slate-900">{displayAmount}</span>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="invoice-description">Description (optional)</Label>
                <Input
                  id="invoice-description"
                  placeholder="e.g. Follow-up consultation"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="invoice-notes">Notes (optional)</Label>
                <Textarea
                  id="invoice-notes"
                  placeholder="Internal notes..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  disabled={isSubmitting}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={isSubmitting || (selectedType === 'custom' && !customAmount)}
                className="bg-[#4A8A9A] hover:bg-[#008f87]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Invoice'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                Invoice created for{' '}
                <span className="font-semibold">
                  ${(createdInvoice.amount / 100).toFixed(2)}
                </span>
                . Share the payment link or send it by email.
              </div>

              {createdInvoice.payment_link && (
                <div className="space-y-2">
                  <Label>Payment Link</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={createdInvoice.payment_link}
                      className="flex-1 truncate text-xs text-slate-600"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyLink}
                      className="shrink-0"
                    >
                      {linkCopied ? (
                        <Check className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={handleClose} disabled={isSendingEmail}>
                Done
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={isSendingEmail}
                className="bg-[#4A8A9A] hover:bg-[#008f87]"
              >
                {isSendingEmail ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send by Email
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BillPatientDialog;
